import type { UseMobileAppPasskeysReturn, AppPasskeyState } from '../types'
import type { DevicePasskey } from '../utils/passkeyDevice'

import {
    ref,
    computed,
    toValue,
    useCoreConfig,
    useDevice,
    useUserInfo,
    useTracking,
    useMobileApp,
    useWebauthn,
    createSharedComposable
} from '#imports'

import { hasPasskeyOnDevice as hasPasskeyOnDeviceForDevice } from '../utils/passkeyDevice'

/**
 * @description The native app passkey bridge: owns window.passKeyFunctions, the
 * mobile auth deferred-promise, the app passkey state, and the pass_keys_*
 * message protocol. Sits on top of the pure-web useWebauthn (which it wraps for
 * every mobile flow) and the detection-only useMobileApp.
 */
export const useMobileAppPasskeys = createSharedComposable((): UseMobileAppPasskeysReturn => {
    const config = useCoreConfig()
    const { isLoggedIn } = useUserInfo()
    const { trackEvent } = useTracking()
    const { userAgent, isIos, isAndroid } = useDevice()
    const { isMobileApp, postToApp } = useMobileApp()
    const webauthn = useWebauthn()

    const appPasskeyState = ref<AppPasskeyState | null>(null)

    let mobileAuthDeferred: (() => void) | null = null

    // Resolve and clear the in-flight mobile-auth promise (if any). Safe to call repeatedly.
    function settleMobileAuth() {
        const resolve = mobileAuthDeferred
        mobileAuthDeferred = null
        resolve?.()
    }

    const shouldShowPassKeyRegistrationModal = computed(() => {
        if (!toValue(isLoggedIn)) return
        if (!isMobileApp) return false

        return appPasskeyState.value === 'NONE'
    })

    function setAppPasskeyState(state: AppPasskeyState) {
        if (!isMobileApp) return

        const stateMap = {
            CREATED: 'pass_keys_registered',
            DISMISSED: 'pass_keys_dismissed',
            NONE: 'pass_keys_cleared',
        }

        appPasskeyState.value = state
        postToApp({ type: stateMap[state] })
    }

    function clearAppPasskeyState() {
        if (!isMobileApp) return
        setAppPasskeyState('NONE')
    }

    function handleNativePasskeyError(error: string) {
        webauthn.registrationState.value.error = error
        webauthn.setLoadingState(false)
        settleMobileAuth()
    }

    async function initiatePassKeyLogin() {
        webauthn.passkeyLoginSucceeded.value = false

        await trackEvent({
            event_name: 'login_passkey',
            event_type: 'Click',
            meta_data: [
                { category: 'Login' },
            ],
        })

        webauthn.registrationState.value.error = null

        const authenticationOptions = await webauthn.getAuthenticationOptions()

        if (!authenticationOptions) {
            webauthn.registrationState.value.error = 'core.defaultErrorMessage'
            webauthn.setLoadingState(false)
            return
        }

        // Release any prior in-flight attempt so a concurrent trigger can't orphan it.
        settleMobileAuth()
        await new Promise<void>((resolve) => {
            mobileAuthDeferred = resolve
            postToApp({
                type: 'pass_keys_authenticate_with_pass_key',
                authenticationOptions
            })
        })
    }

    function hasPasskeyOnDevice(passkeys: DevicePasskey[]): boolean {
        return hasPasskeyOnDeviceForDevice(userAgent, isIos, isAndroid, passkeys)
    }

    function setupMobileAppIntegration() {
        if (!import.meta.client || !config.webauthn?.enabled || !isMobileApp) return

        window.passKeyFunctions = {
            showModal: () => { webauthn.showPassKeysModal() },
            closeModal: () => { webauthn.closeModal() },
            rejectPassKey: () => { webauthn.markPassKeyAsRejected() },
            passKeyRegistered: () => {
                webauthn.markPassKeyAsRegistered()
                setAppPasskeyState('CREATED')
            },
            verifyRegistration: async (response) => {
                // CREATED only when THIS verify succeeded — mirrors the old
                // success-only setAppPasskeyState('CREATED') without relying on
                // the persistent isRegistered flag (stale after a prior success).
                const registered = await webauthn.verifyRegistration(response)
                if (registered) {
                    setAppPasskeyState('CREATED')
                }
            },
            initiatePassKeyLogin: () => { initiatePassKeyLogin() },
            setAppPasskeyStatus: (state: AppPasskeyState) => { setAppPasskeyState(state) },
            loginWithAuthenticationResponse: async (authOptions) => {
                // settle in finally so a thrown trackEvent/reload can't orphan the
                // deferred promise and hang the native loading sheet.
                try {
                    const result = await webauthn.authenticateWithWebAuthn(authOptions)
                    if (!result.ok && result.reason === 'KEY_NOT_FOUND') {
                        clearAppPasskeyState()
                    }
                }
                finally {
                    settleMobileAuth()
                }
            },
            handleNativePasskeyError: (error: string) => { handleNativePasskeyError(error) },
            setPasskeyLoading: (loading: boolean) => {
                webauthn.setLoadingState(loading)
                if (!loading) {
                    settleMobileAuth()
                }
            },
        }

        if (shouldShowPassKeyRegistrationModal.value) {
            postToApp({ type: 'pass_keys_initiate' })
        }
    }

    function init() {
        setupMobileAppIntegration()
    }

    return {
        appPasskeyState,
        shouldShowPassKeyRegistrationModal,
        initiatePassKeyLogin,
        setAppPasskeyState,
        clearAppPasskeyState,
        handleNativePasskeyError,
        hasPasskeyOnDevice,
        init,
    }
})
